/*!
 * Recharge Subscription Widget — SPv2 (Checkout on Shopify)
 * Vanilla JS, no dependencies. Upload to Shopify Assets.
 *
 * Auto-detects two product shapes and renders accordingly:
 *
 *   A) Multi-variant (each variant has its own selling plan):
 *      → Full tier cards with radio selection, savings badges,
 *        "Subscribe and Save" + "Purchase Once" buttons, trial link.
 *
 *   B) Single-variant (one variant with multiple selling plans):
 *      → Radio toggle: One-time purchase vs Subscribe & save.
 *        When subscribe is selected, interval pills appear (30, 45, 60, 90 days…).
 *        Single "Add to Cart" button.
 *
 * Mount: <div id="recharge-widget" data-product="..." data-trial-url="..." data-supply-copy="..."></div>
 */
(function () {
  'use strict';

  var RECHARGE_APP_ID = '294517';

  // ---------- interval parsing ----------------------------------------------

  function parseInterval(value) {
    if (!value) return null;
    var normalized = value.toLowerCase().replace(/\s+/g, '-');

    var match = normalized.match(/^(\d+)-(day|days|week|weeks|month|months)$/);
    if (!match) {
      if (normalized === 'monthly') return { count: 1, unit: 'month', days: 30, key: 'monthly' };
      return null;
    }

    var count = parseInt(match[1], 10);
    var unit = match[2].replace(/s$/, '');
    var days;

    if (unit === 'day') days = count;
    else if (unit === 'week') days = count * 7;
    else if (unit === 'month') days = count * 30;
    else return null;

    var key;
    if (unit === 'month' && count === 1) key = 'monthly';
    else key = count + unit;

    return { count: count, unit: unit, days: days, key: key };
  }

  function defaultSupply(parsed) {
    if (!parsed) return '';
    if (parsed.unit === 'month') {
      if (parsed.count === 1) return 'Receive a 1-month supply each month.';
      return 'Receive a ' + parsed.count + '-month supply every ' + parsed.count + ' months.';
    }
    if (parsed.unit === 'week') {
      return 'Receive a supply every ' + parsed.count + ' week' + (parsed.count > 1 ? 's' : '') + '.';
    }
    return 'Receive a supply every ' + parsed.count + ' day' + (parsed.count > 1 ? 's' : '') + '.';
  }

  // Build a short pill label like "30\ndays" or "3\nmonths"
  function pillLabel(parsed) {
    if (!parsed) return '';
    var unitLabel = parsed.unit + (parsed.count > 1 ? 's' : '');
    return parsed.count + '\n' + unitLabel;
  }

  // ---------- tiers ---------------------------------------------------------

  function buildTiers(product, rechargeAppId, supplyCopy) {
    supplyCopy = supplyCopy || {};
    var tiers = [];
    var variants = (product && product.variants) || [];
    var groups = (product && product.selling_plan_groups) || [];

    for (var v = 0; v < variants.length; v++) {
      var variant = variants[v];
      if (!variant.available) continue;

      var allocations = variant.selling_plan_allocations || [];

      for (var a = 0; a < allocations.length; a++) {
        var allocation = allocations[a];

        var group = findById(groups, allocation.selling_plan_group_id);
        if (!group) continue;
        if (String(group.app_id) !== String(rechargeAppId)) continue;

        var plan = findPlan(group, allocation.selling_plan_id);
        var interval = extractInterval(group, plan);
        var parsed = parseInterval(interval);
        if (!parsed) continue;

        var subscriptionPrice = allocation.price;
        var oneTimePrice = variant.price;
        var compareAt =
          allocation.compare_at_price && allocation.compare_at_price > 0
            ? allocation.compare_at_price
            : oneTimePrice;

        var savePercent =
          compareAt > 0
            ? Math.round((1 - subscriptionPrice / compareAt) * 100)
            : 0;

        var label = (plan && plan.name) || group.name || variant.title;

        tiers.push({
          key: parsed.key,
          label: label,
          supply: supplyCopy[parsed.key] || defaultSupply(parsed),
          variantId: variant.id,
          sellingPlanId: allocation.selling_plan_id,
          subscriptionPrice: subscriptionPrice,
          oneTimePrice: oneTimePrice,
          savePercent: savePercent,
          intervalDays: parsed.days,
          interval: parsed,
        });
      }
    }

    tiers.sort(function (a, b) {
      return a.intervalDays - b.intervalDays;
    });

    var seen = {};
    return tiers.filter(function (t) {
      if (seen[t.sellingPlanId]) return false;
      seen[t.sellingPlanId] = true;
      return true;
    });
  }

  // Detect whether tiers all belong to the same variant (single-variant mode)
  // or span multiple variants (multi-variant mode).
  function detectLayout(tiers) {
    if (tiers.length === 0) return 'multi';
    var firstVariant = tiers[0].variantId;
    for (var i = 1; i < tiers.length; i++) {
      if (tiers[i].variantId !== firstVariant) return 'multi';
    }
    return 'single';
  }

  function findById(list, id) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === id) return list[i];
    }
    return null;
  }

  function findPlan(group, planId) {
    var plans = group.selling_plans || [];
    for (var i = 0; i < plans.length; i++) {
      if (plans[i].id === planId) return plans[i];
    }
    return null;
  }

  function extractInterval(group, plan) {
    if (plan && plan.options) {
      for (var i = 0; i < plan.options.length; i++) {
        var o = plan.options[i];
        if (o && o.name && /frequency|interval|unit/i.test(o.name)) {
          return (o.value || '').toLowerCase();
        }
      }
    }
    var opts = group.options || [];
    for (var j = 0; j < opts.length; j++) {
      var og = opts[j];
      if (og && og.name && /frequency|interval|unit/i.test(og.name)) {
        return ((og.values && og.values[0]) || '').toLowerCase();
      }
    }
    return '';
  }

  // ---------- cart ----------------------------------------------------------

  function addToCart(item) {
    var body = { items: [cleanItem(item)] };
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) {
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            var msg =
              (data && (data.description || data.message)) ||
              'Add to cart failed (' + res.status + ')';
            throw new Error(msg);
          });
      }
      return res.json();
    });
  }

  function cleanItem(item) {
    var out = { id: item.id, quantity: item.quantity || 1 };
    if (item.selling_plan) out.selling_plan = item.selling_plan;
    return out;
  }

  function refreshCart() {
    if (window.console) {
      console.log('[recharge-widget] refreshing cart, Rebuy available:',
        !!(window.Rebuy && window.Rebuy.SmartCart));
    }

    // 1. Fetch fresh cart state from Shopify so all downstream consumers
    //    (Rebuy, Dawn, other scripts) see the newly-added line item.
    return fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        if (window.console) {
          console.log('[recharge-widget] cart fetched, items:',
            cart ? cart.item_count : 'n/a');
        }

        // 2. Dispatch events FIRST — Rebuy SmartCart may auto-open on
        //    cart.change in some configurations. Firing events before
        //    the manual show() lets that path work naturally.
        try {
          document.dispatchEvent(new CustomEvent('rebuy:cart.change', { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent('rebuy:cart.refresh'));
          window.dispatchEvent(new CustomEvent('cart:build'));
        } catch (err) {
          if (window.console) console.warn('[recharge-widget] event dispatch error:', err);
        }

        // 3. Rebuy SmartCart — refresh data, then show the drawer.
        try {
          if (window.Rebuy && window.Rebuy.SmartCart) {
            var refreshResult;
            if (typeof window.Rebuy.SmartCart.refresh === 'function') {
              refreshResult = window.Rebuy.SmartCart.refresh();
            }
            // If refresh returned a thenable, wait for it before showing.
            if (refreshResult && typeof refreshResult.then === 'function') {
              refreshResult.then(function () {
                showRebuyCart();
              }).catch(function () {
                // Still try to show even if refresh failed
                showRebuyCart();
              });
            } else {
              showRebuyCart();
            }
          } else {
            // Rebuy may not be loaded yet — retry after a short delay.
            if (window.console) {
              console.log('[recharge-widget] Rebuy.SmartCart not found, retrying in 500ms');
            }
            setTimeout(function () {
              showRebuyCart();
            }, 500);
          }
        } catch (err) {
          if (window.console) console.warn('[recharge-widget] Rebuy refresh failed:', err);
          // Fallback: try show after a delay
          setTimeout(function () { showRebuyCart(); }, 300);
        }
      });
  }

  function showRebuyCart() {
    try {
      // Method 1: Rebuy.SmartCart.show()
      if (window.Rebuy && window.Rebuy.SmartCart &&
          typeof window.Rebuy.SmartCart.show === 'function') {
        if (window.console) console.log('[recharge-widget] calling Rebuy.SmartCart.show()');
        window.Rebuy.SmartCart.show();
        return;
      }

      // Method 2: Click the Rebuy cart toggle button if it exists in the DOM
      var rebuyToggle = document.querySelector('[data-rebuy-toggle="smartcart"]') ||
                        document.querySelector('.rebuy-cart__flyout-toggle') ||
                        document.querySelector('#rebuy-smart-cart-toggle');
      if (rebuyToggle) {
        if (window.console) console.log('[recharge-widget] clicking Rebuy toggle element');
        rebuyToggle.click();
        return;
      }

      if (window.console) {
        console.warn('[recharge-widget] could not find Rebuy SmartCart to open');
      }
    } catch (err) {
      if (window.console) console.warn('[recharge-widget] Rebuy show failed:', err);
    }
  }

  // ---------- widget (shared) -----------------------------------------------

  function mount(el, config) {
    var product = config.product;
    var tiers = buildTiers(product, config.rechargeAppId, config.supplyCopy);

    if (tiers.length === 0) {
      if (window.console) {
        console.warn(
          '[recharge-widget] no Recharge tiers found for product',
          product && product.handle
        );
      }
      return;
    }

    var layout = detectLayout(tiers);

    // Pick the initial tier:
    //   1. Explicit override from data-default-tier (settable per product
    //      via a metafield in the Liquid snippet).
    //   2. For multi-variant layouts, prefer the 3-month tier if present.
    //   3. Otherwise fall back to the first tier (shortest interval).
    var defaultTier = tiers[0].key;
    if (layout === 'multi' && findTier(tiers, '3month')) {
      defaultTier = '3month';
    }
    if (config.defaultTier && findTier(tiers, config.defaultTier)) {
      defaultTier = config.defaultTier;
    }

    var state = {
      selectedTier: defaultTier,
      purchaseType: 'subscription',
      submitting: false,
      error: null,
    };

    document.body.classList.add('rc-widget-active');

    var root = document.createElement('div');
    root.className = 'rc-widget' + (layout === 'single' ? ' rc-widget--single' : ' rc-widget--multi');
    el.appendChild(root);

    // Track the last variant we synced so we don't re-sync on unrelated
    // re-renders (e.g. the submit spinner toggling).
    var lastSyncedVariantId = null;

    function render() {
      var tier = findTier(tiers, state.selectedTier) || tiers[0];
      if (layout === 'single') {
        root.innerHTML = templateSingle(tiers, tier, state, config.trialUrl);
        bindSingle(root, tiers, state, render);
      } else {
        root.innerHTML = templateMulti(tiers, tier, state, config.trialUrl);
        bindMulti(root, tiers, state, render);
      }

      // For multi-variant layouts, tell Dawn about the variant change so
      // its media gallery, price block, URL, etc. update. Single-variant
      // products never change the variantId, so this is a no-op there.
      if (layout === 'multi' && tier.variantId !== lastSyncedVariantId) {
        lastSyncedVariantId = tier.variantId;
        syncVariantSelection(product, tier.variantId);
      }
    }

    render();
  }

  // Notify the host theme (Dawn or similar) that a new variant is selected.
  // We do this by finding Dawn's variant inputs and firing the standard
  // change event they already listen to. This is what triggers the featured
  // image swap, price update, and inventory refresh in Dawn.
  function syncVariantSelection(product, variantId) {
    try {
      // 1. Update the URL so shares / refreshes land on the right variant.
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }

      // 2. Find the variant object so we can match against Dawn's option inputs.
      var variant = null;
      var variants = (product && product.variants) || [];
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === variantId) { variant = variants[i]; break; }
      }
      if (!variant) return;

      // 3. Update Dawn's <variant-selects> / variant radios / selects and
      //    dispatch a change event so Dawn's built-in JS picks it up.
      //    Dawn markup varies by version — cover both radios and selects.
      var updated = false;

      // Radios: each option value has its own <input type="radio">
      var radios = document.querySelectorAll(
        'variant-radios input[type="radio"], .product-form__input input[type="radio"]'
      );
      for (var r = 0; r < radios.length; r++) {
        var radio = radios[r];
        // Match by value + fieldset (option position) if available.
        var fieldset = radio.closest('fieldset');
        var optionIndex = fieldset ? parseInt(fieldset.dataset.optionIndex || '', 10) : NaN;
        var targetValue = variant.options && variant.options[optionIndex - 1];
        if (targetValue && radio.value === targetValue && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          updated = true;
        } else if (!fieldset && variant.options && variant.options.indexOf(radio.value) !== -1 && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          updated = true;
        }
      }

      // Selects: <variant-selects> wrapping <select> elements
      var selects = document.querySelectorAll(
        'variant-selects select, .product-form__input select'
      );
      for (var s = 0; s < selects.length; s++) {
        var select = selects[s];
        var idx = parseInt(select.dataset.optionIndex || select.name || '', 10);
        var val = variant.options && variant.options[idx - 1];
        if (val && select.value !== val) {
          // Try matching by value or by title
          for (var o = 0; o < select.options.length; o++) {
            if (select.options[o].value === val || select.options[o].text === val) {
              select.selectedIndex = o;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              updated = true;
              break;
            }
          }
        }
      }

      // 4. Fallback: if we couldn't find Dawn's inputs, swap the featured
      //    image directly by matching the variant's featured_image.
      if (!updated && variant.featured_image && variant.featured_image.src) {
        swapFeaturedImage(variant);
      }

      // 5. Fire a generic variant:change event that custom themes may listen to.
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant: variant, product: product },
        bubbles: true,
      }));
    } catch (err) {
      if (window.console) console.warn('[recharge-widget] variant sync failed:', err);
    }
  }

  // Fallback featured-image swap for themes where Dawn's variant inputs
  // aren't present. Finds the main product image and updates its src.
  function swapFeaturedImage(variant) {
    var src = variant.featured_image && variant.featured_image.src;
    if (!src) return;

    // Common selectors for the main product image across Dawn versions.
    var candidates = document.querySelectorAll(
      '.product__media img, .product__media-item img, ' +
      '.product-single__photo img, .product__photo img, ' +
      'media-gallery img[data-media-id]'
    );
    if (candidates.length === 0) return;

    // Update the first (featured) image. srcset gets cleared so the new
    // src wins across all breakpoints.
    var img = candidates[0];
    img.src = src;
    img.removeAttribute('srcset');
  }

  function findTier(tiers, key) {
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].key === key) return tiers[i];
    }
    return null;
  }

  // ---------- multi-variant layout ------------------------------------------
  // Full tier cards with radio selection, two buttons (subscribe / purchase once)

  function templateMulti(tiers, tier, state, trialUrl) {
    var cards = tiers
      .map(function (t) {
        return tierCard(t, state.selectedTier === t.key);
      })
      .join('');

    var subscribingNow =
      state.submitting && state.purchaseType === 'subscription';
    var onetimeNow = state.submitting && state.purchaseType === 'onetime';

    var trialSection = '';
    if (trialUrl && trialUrl !== '#' && trialUrl !== '') {
      trialSection =
        '<div class="rc-trial">' +
        '<div class="rc-trial__label">Still unsure?</div>' +
        '<a class="rc-trial__link" href="' +
        escapeAttr(trialUrl) +
        '">15-day trial available - $25</a>' +
        '<div class="rc-trial__note">*Use product for 3 months for best results</div>' +
        '</div>';
    }

    return (
      '<div class="rc-tiers" role="radiogroup" aria-label="Delivery frequency">' +
      cards +
      '</div>' +
      '<button type="button" class="rc-btn rc-btn--primary" data-action="subscribe"' +
      (state.submitting ? ' disabled' : '') +
      ' aria-busy="' + subscribingNow + '">' +
      (subscribingNow ? 'Adding\u2026' : 'Subscribe and Save') +
      '</button>' +
      '<button type="button" class="rc-btn rc-btn--secondary" data-action="onetime"' +
      (state.submitting ? ' disabled' : '') +
      ' aria-busy="' + onetimeNow + '">' +
      (onetimeNow ? 'Adding\u2026' : 'Purchase Once - ' + money(tier.oneTimePrice)) +
      '</button>' +
      (state.error
        ? '<div class="rc-error" role="alert">' + escapeHtml(state.error) + '</div>'
        : '') +
      trialSection
    );
  }

  function tierCard(tier, selected) {
    return (
      '<label class="rc-tier' +
      (selected ? ' rc-tier--selected' : '') +
      '" data-tier="' + tier.key + '">' +
      '<input type="radio" name="rc-tier" value="' + tier.key + '"' +
      (selected ? ' checked' : '') + ' tabindex="-1">' +
      '<span class="rc-tier__radio" aria-hidden="true"></span>' +
      '<div class="rc-tier__body">' +
      '<div class="rc-tier__header">' +
      '<span class="rc-tier__title">' + escapeHtml(tier.label) + '</span>' +
      (tier.savePercent > 0
        ? '<span class="rc-tier__save">SAVE ' + tier.savePercent + '%</span>'
        : '') +
      '</div>' +
      '<div class="rc-tier__supply">' + escapeHtml(tier.supply) + '</div>' +
      '<div class="rc-tier__meta">Free shipping + free gift on first order</div>' +
      '</div>' +
      '<div class="rc-tier__prices">' +
      '<span class="rc-tier__price">' + money(tier.subscriptionPrice) + '</span>' +
      (tier.oneTimePrice !== tier.subscriptionPrice
        ? '<span class="rc-tier__compare">' + money(tier.oneTimePrice) + '</span>'
        : '') +
      '</div>' +
      '</label>'
    );
  }

  function bindMulti(root, tiers, state, render) {
    var cards = root.querySelectorAll('[data-tier]');
    for (var i = 0; i < cards.length; i++) {
      (function (label) {
        label.addEventListener('click', function (e) {
          e.preventDefault();
          var key = label.getAttribute('data-tier');
          if (key && key !== state.selectedTier) {
            state.selectedTier = key;
            render();
          }
        });
      })(cards[i]);
    }

    var subBtn = root.querySelector('[data-action="subscribe"]');
    if (subBtn) {
      subBtn.addEventListener('click', function () {
        submit('subscription', tiers, state, render);
      });
    }

    var onceBtn = root.querySelector('[data-action="onetime"]');
    if (onceBtn) {
      onceBtn.addEventListener('click', function () {
        submit('onetime', tiers, state, render);
      });
    }
  }

  // ---------- single-variant layout -----------------------------------------
  // Radio toggle (one-time vs subscribe), interval pills, single ATC button

  function templateSingle(tiers, tier, state, trialUrl) {
    var isSubscribe = state.purchaseType === 'subscription';
    var isOnetime = state.purchaseType === 'onetime';

    // Use the best savings to advertise in the subscribe option
    var bestSave = 0;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].savePercent > bestSave) bestSave = tiers[i].savePercent;
    }

    // Radio options
    var onetimeOption =
      '<label class="rc-option' + (isOnetime ? ' rc-option--selected' : '') + '" data-purchase="onetime">' +
      '<input type="radio" name="rc-purchase" value="onetime"' + (isOnetime ? ' checked' : '') + ' tabindex="-1">' +
      '<span class="rc-option__radio" aria-hidden="true"></span>' +
      '<div class="rc-option__body">' +
      '<div class="rc-option__title">One-time purchase</div>' +
      '<div class="rc-option__detail">' +
      money(tier.oneTimePrice) + ' - Purchase this time only' +
      '</div>' +
      '</div>' +
      '</label>';

    var subscribeOption =
      '<label class="rc-option' + (isSubscribe ? ' rc-option--selected' : '') + '" data-purchase="subscription">' +
      '<input type="radio" name="rc-purchase" value="subscription"' + (isSubscribe ? ' checked' : '') + ' tabindex="-1">' +
      '<span class="rc-option__radio" aria-hidden="true"></span>' +
      '<div class="rc-option__body">' +
      '<div class="rc-option__title">Subscribe & save</div>' +
      '<div class="rc-option__detail">' +
      money(tier.subscriptionPrice) +
      (tier.savePercent > 0 ? ' - Get ' + tier.savePercent + '% off + free shipping' : ' + free shipping') +
      '</div>' +
      '</div>' +
      '</label>';

    // Interval pills (only visible when subscribe is selected)
    var pillsHtml = '';
    if (isSubscribe) {
      var pills = tiers
        .map(function (t) {
          var sel = t.key === state.selectedTier;
          var p = t.interval;
          return (
            '<button type="button" class="rc-pill' +
            (sel ? ' rc-pill--selected' : '') +
            '" data-tier="' + t.key + '">' +
            '<span class="rc-pill__count">' + p.count + '</span>' +
            '<span class="rc-pill__unit">' + p.unit + (p.count > 1 ? 's' : '') + '</span>' +
            '</button>'
          );
        })
        .join('');

      pillsHtml =
        '<div class="rc-pills" role="radiogroup" aria-label="Delivery interval">' +
        pills +
        '</div>';
    }

    // Single Add to Cart button
    var btnLabel;
    var btnPrice;
    if (isSubscribe) {
      btnLabel = 'Subscribe and Save';
      btnPrice = money(tier.subscriptionPrice);
    } else {
      btnLabel = 'Add to Cart';
      btnPrice = money(tier.oneTimePrice);
    }

    var trialSection = '';
    if (trialUrl && trialUrl !== '#' && trialUrl !== '') {
      trialSection =
        '<div class="rc-trial">' +
        '<div class="rc-trial__label">Still unsure?</div>' +
        '<a class="rc-trial__link" href="' + escapeAttr(trialUrl) + '">15-day trial available - $25</a>' +
        '<div class="rc-trial__note">*Use product for 3 months for best results</div>' +
        '</div>';
    }

    return (
      '<div class="rc-options" role="radiogroup" aria-label="Purchase type">' +
      onetimeOption +
      subscribeOption +
      pillsHtml +
      '</div>' +
      '<button type="button" class="rc-btn rc-btn--primary" data-action="add"' +
      (state.submitting ? ' disabled' : '') +
      ' aria-busy="' + state.submitting + '">' +
      (state.submitting ? 'Adding\u2026' : btnLabel + ' - ' + btnPrice) +
      '</button>' +
      (state.error
        ? '<div class="rc-error" role="alert">' + escapeHtml(state.error) + '</div>'
        : '') +
      trialSection
    );
  }

  function bindSingle(root, tiers, state, render) {
    // Purchase type radios
    var options = root.querySelectorAll('[data-purchase]');
    for (var i = 0; i < options.length; i++) {
      (function (label) {
        label.addEventListener('click', function (e) {
          e.preventDefault();
          var type = label.getAttribute('data-purchase');
          if (type && type !== state.purchaseType) {
            state.purchaseType = type;
            render();
          }
        });
      })(options[i]);
    }

    // Interval pills
    var pills = root.querySelectorAll('[data-tier]');
    for (var j = 0; j < pills.length; j++) {
      (function (pill) {
        pill.addEventListener('click', function (e) {
          e.preventDefault();
          var key = pill.getAttribute('data-tier');
          if (key && key !== state.selectedTier) {
            state.selectedTier = key;
            render();
          }
        });
      })(pills[j]);
    }

    // Add to cart button
    var addBtn = root.querySelector('[data-action="add"]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        submit(state.purchaseType, tiers, state, render);
      });
    }
  }

  // ---------- shared submit -------------------------------------------------

  function submit(type, tiers, state, render) {
    if (state.submitting) return;
    var tier = findTier(tiers, state.selectedTier);
    if (!tier) {
      if (window.console) {
        console.warn('[recharge-widget] no tier found for key:', state.selectedTier,
          'available:', tiers.map(function (t) { return t.key; }));
      }
      return;
    }

    var payload = {
      id: tier.variantId,
      quantity: 1,
    };
    if (type === 'subscription') {
      payload.selling_plan = tier.sellingPlanId;
    }

    if (window.console) {
      console.log('[recharge-widget] adding to cart:', JSON.stringify(payload));
    }

    state.submitting = true;
    state.purchaseType = type;
    state.error = null;
    render();

    addToCart(payload)
      .then(function (response) {
        if (window.console) {
          console.log('[recharge-widget] add to cart success:', response);
        }
        // Chain refreshCart so the spinner stays visible until the Rebuy
        // drawer is open and the cart data is fresh.
        return refreshCart();
      })
      .catch(function (err) {
        if (window.console) {
          console.error('[recharge-widget] add to cart error:', err);
        }
        state.error = (err && err.message) || 'Could not add to cart.';
      })
      .then(function () {
        state.submitting = false;
        render();
      });
  }

  // ---------- utilities -----------------------------------------------------

  function money(cents) {
    if (
      typeof window !== 'undefined' &&
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function'
    ) {
      try {
        return window.Shopify.formatMoney(cents);
      } catch (e) {
        /* fall through */
      }
    }
    var dollars = cents / 100;
    return '$' + (dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2));
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c
      );
    });
  }

  function escapeAttr(str) {
    return escapeHtml(str);
  }

  function parseJson(value) {
    if (!value) return {};
    try {
      return JSON.parse(value);
    } catch (err) {
      return {};
    }
  }

  // ---------- boot ----------------------------------------------------------

  function init() {
    var el = document.getElementById('recharge-widget');
    if (!el) return;

    var product = parseJson(el.getAttribute('data-product'));
    if (!product || !product.variants) {
      if (window.console) {
        console.error('[recharge-widget] invalid or missing data-product');
      }
      return;
    }

    mount(el, {
      product: product,
      rechargeAppId: RECHARGE_APP_ID,
      trialUrl: el.getAttribute('data-trial-url') || '',
      supplyCopy: parseJson(el.getAttribute('data-supply-copy')),
      defaultTier: el.getAttribute('data-default-tier') || '',
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
